/**
 * Frame resolution: turns the editable document into a flat, renderer-agnostic description of what
 * is visible at one frame. Both the realtime preview and the exporter render from this description,
 * which guarantees that what you see is what you export.
 */
import { evaluateParam } from '../animation/keyframes.ts';
import { framesToSeconds } from '../model/time.ts';
import type {
  BlendMode,
  Clip,
  FitMode,
  Id,
  Project,
  ProjectSettings,
  Sequence,
  TextStyle,
  Track,
  TransitionType,
  VisualClip,
} from '../model/schema.ts';
import { clipAt, sequenceDuration, transitionRange } from '../timeline/queries.ts';

export const MAX_NESTING_DEPTH = 16;

export type VisualSource =
  | { type: 'video'; mediaId: Id; /** seconds into the media */ time: number }
  | { type: 'image'; mediaId: Id }
  | { type: 'text'; text: string; style: TextStyle }
  | { type: 'solid'; color: string }
  | { type: 'sequence'; sequenceId: Id; frame: number; layers: RenderNode[] };

export interface ResolvedTransform {
  /** Offset of the layer centre from the canvas centre, px. */
  x: number;
  y: number;
  /** Final scale factors (uniform scale × axis scale). */
  scaleX: number;
  scaleY: number;
  /** Degrees, clockwise. */
  rotation: number;
  /** Fractions (0..1) of the source to crop from each edge. */
  crop: { left: number; right: number; top: number; bottom: number };
}

export interface LayerNode {
  kind: 'layer';
  /** Unique, stable path for this layer instance (clip ids joined through nesting). */
  key: string;
  clipId: Id;
  source: VisualSource;
  fit: FitMode;
  blendMode: BlendMode;
  opacity: number;
  transform: ResolvedTransform;
}

export interface TransitionNode {
  kind: 'transition';
  key: string;
  type: TransitionType;
  /** 0 → fully `from`, 1 → fully `to`. */
  progress: number;
  from: LayerNode | null;
  to: LayerNode | null;
}

export type RenderNode = LayerNode | TransitionNode;

export interface FrameDescription {
  frame: number;
  width: number;
  height: number;
  background: string;
  /** Colour space layers are blended in; see `ProjectSettings.blending`. Defaults to linear. */
  blending?: ProjectSettings['blending'];
  /** Bottom-most first. */
  layers: RenderNode[];
}

function isVisual(clip: Clip): clip is VisualClip {
  return clip.type !== 'audio';
}

function resolveLayer(
  project: Project,
  clip: VisualClip,
  frame: number,
  keyPrefix: string,
  depth: number,
): LayerNode | null {
  const rel = frame - clip.start;
  const p = (k: Parameters<typeof evaluateParam>[1]) => evaluateParam(clip, k, rel);
  const key = keyPrefix + clip.id;
  let source: VisualSource;
  switch (clip.type) {
    case 'video': {
      const media = project.media[clip.mediaId];
      if (!media) return null;
      const raw = clip.sourceIn + framesToSeconds(rel, project.settings.fps) * clip.speed;
      const frameDur = 1 / (media.fps ?? 30);
      const max = media.duration !== undefined ? Math.max(0, media.duration - frameDur) : raw;
      source = { type: 'video', mediaId: clip.mediaId, time: Math.min(Math.max(0, raw), max) };
      break;
    }
    case 'image':
      if (!project.media[clip.mediaId]) return null;
      source = { type: 'image', mediaId: clip.mediaId };
      break;
    case 'text':
      source = { type: 'text', text: clip.text, style: clip.style };
      break;
    case 'solid':
      source = { type: 'solid', color: clip.color };
      break;
    case 'sequence': {
      const child = project.sequences[clip.sequenceId];
      if (!child || depth >= MAX_NESTING_DEPTH) return null;
      const childFrame = clip.sourceIn + rel;
      const inRange = childFrame >= 0 && childFrame < sequenceDuration(project, child);
      source = {
        type: 'sequence',
        sequenceId: child.id,
        frame: childFrame,
        layers: inRange ? resolveSequenceLayers(project, child, childFrame, key + '/', depth + 1) : [],
      };
      break;
    }
  }
  const scale = p('scale');
  return {
    kind: 'layer',
    key,
    clipId: clip.id,
    source,
    fit: clip.fit,
    blendMode: clip.blendMode,
    opacity: Math.min(1, Math.max(0, p('opacity'))),
    transform: {
      x: p('x'),
      y: p('y'),
      scaleX: scale * p('scaleX'),
      scaleY: scale * p('scaleY'),
      rotation: p('rotation'),
      crop: { left: p('cropLeft'), right: p('cropRight'), top: p('cropTop'), bottom: p('cropBottom') },
    },
  };
}

function resolveTrack(
  project: Project,
  track: Track,
  frame: number,
  keyPrefix: string,
  depth: number,
): RenderNode | null {
  for (const tr of track.transitions) {
    const range = transitionRange(track, tr);
    if (!range || frame < range.start || frame >= range.end) continue;
    const from = track.clips.find((c) => c.id === tr.fromClipId);
    const to = track.clips.find((c) => c.id === tr.toClipId);
    const layer = (c: Clip | undefined) =>
      c && c.enabled && isVisual(c) ? resolveLayer(project, c, frame, keyPrefix, depth) : null;
    return {
      kind: 'transition',
      key: keyPrefix + tr.id,
      type: tr.type,
      progress: (frame - range.start + 0.5) / tr.duration,
      from: layer(from),
      to: layer(to),
    };
  }
  const clip = clipAt(track, frame);
  if (!clip || !clip.enabled || !isVisual(clip)) return null;
  return resolveLayer(project, clip, frame, keyPrefix, depth);
}

function resolveSequenceLayers(
  project: Project,
  seq: Sequence,
  frame: number,
  keyPrefix: string,
  depth: number,
): RenderNode[] {
  const out: RenderNode[] = [];
  for (const track of seq.videoTracks) {
    if (track.hidden) continue;
    const node = resolveTrack(project, track, frame, keyPrefix, depth);
    if (node) out.push(node);
  }
  return out;
}

/** Resolves everything visible in `sequenceId` at `frame`. */
export function resolveFrame(project: Project, sequenceId: Id, frame: number): FrameDescription {
  const seq = project.sequences[sequenceId];
  const { width, height, backgroundColor, blending } = project.settings;
  return {
    frame,
    width,
    height,
    background: backgroundColor,
    blending,
    layers: seq ? resolveSequenceLayers(project, seq, frame, '', 0) : [],
  };
}

/** Visits every layer in a description, including those nested in sequences and transitions. */
export function forEachLayer(nodes: RenderNode[], visit: (layer: LayerNode) => void): void {
  for (const node of nodes) {
    const layers = node.kind === 'layer' ? [node] : [node.from, node.to];
    for (const layer of layers) {
      if (!layer) continue;
      visit(layer);
      if (layer.source.type === 'sequence') forEachLayer(layer.source.layers, visit);
    }
  }
}
